import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { letterApi, letterTemplateApi, customerApi, supplierApi, formatApiError } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Plus, PencilSimple, Trash, FilePdf, EnvelopeSimple, Archive, Sparkle, FileText, Copy,
  User, Buildings, MapPin, CheckCircle, Clock, Warning,
} from '@phosphor-icons/react';

const STATUS_LABELS = { draft: 'Entwurf', sent: 'Gesendet', archived: 'Archiviert' };
const STATUS_STYLES = {
  draft: 'bg-[#F7F8FA] text-[#525866] border-[#E5E7EB]',
  sent: 'bg-[#E6F7EB] text-[#009951] border-[#B7E4C7]',
  archived: 'bg-[#F0F1F3] text-[#8A94A6] border-[#E5E7EB]',
};

const RECIPIENT_ICONS = { customer: User, supplier: Buildings, freeform: MapPin };
const RECIPIENT_LABELS = { customer: 'Kunde', supplier: 'Lieferant', freeform: 'Frei' };

const emptyRecipient = { type: 'customer', id: '', company: '', name: '', address: '', postal_code: '', city: '', country: 'Deutschland', email: '', salutation: '' };
const emptyLetter = { subject: '', body: '', recipient: { ...emptyRecipient }, sender_name: '', related_type: '', related_id: '', template_id: '', notes_admin: '' };

const Letters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [editor, setEditor] = useState(null);          // active letter being edited (draft object)
  const [editorSaving, setEditorSaving] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiForm, setAiForm] = useState({ intent: '', tone: 'formell', keywords: '', recipient_hint: '', context: '' });
  const [aiRunning, setAiRunning] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveAsTpl, setShowSaveAsTpl] = useState(false);
  const [newTplName, setNewTplName] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ls, ts, cs, ss] = await Promise.all([
        letterApi.list(statusFilter !== 'all' ? { status: statusFilter } : {}),
        letterTemplateApi.list({}),
        customerApi.getAll(),
        supplierApi.getAll().catch(() => ({ data: [] })),
      ]);
      setLetters(ls.data || []);
      setTemplates(ts.data || []);
      setCustomers(cs.data || []);
      setSuppliers(ss.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Deep-Link: /letters?new=1&customer_id=X&booking_id=Y (oder supplier_id / ticket_id)
  // ODER: /letters?open=<letterId>  → öffnet bestehenden Brief im Editor
  useEffect(() => {
    if (loading) return;
    if (editor) return;

    const openId = searchParams.get('open');
    if (openId) {
      const existing = letters.find((l) => l.id === openId);
      if (existing) {
        openExisting(existing);
        const sp = new URLSearchParams(searchParams);
        sp.delete('open');
        setSearchParams(sp, { replace: true });
        return;
      }
    }

    if (searchParams.get('new') !== '1') return;
    const custId = searchParams.get('customer_id');
    const suppId = searchParams.get('supplier_id');
    const bookingId = searchParams.get('booking_id');
    const ticketId = searchParams.get('ticket_id');
    const invoiceId = searchParams.get('invoice_id');

    let recipient = { ...emptyRecipient };
    if (custId) {
      const c = customers.find((x) => x.id === custId);
      if (c) {
        recipient = {
          ...emptyRecipient,
          type: 'customer', id: custId,
          name: `${c.first_name} ${c.last_name}`.trim(),
          company: c.customer_type === 'business' ? (c.company_name || '') : '',
          address: c.address || '', postal_code: c.postal_code || '',
          city: c.city || '', country: c.country || 'Deutschland',
          email: c.email || '',
        };
      }
    } else if (suppId) {
      const s = suppliers.find((x) => x.id === suppId);
      if (s) {
        recipient = {
          ...emptyRecipient,
          type: 'supplier', id: suppId,
          company: s.name || s.company || '',
          name: s.contact_name || s.name || '',
          address: s.address || '', postal_code: s.postal_code || '',
          city: s.city || '', country: s.country || 'Deutschland',
          email: s.email || '',
        };
      }
    }
    const related_type = bookingId ? 'booking' : (ticketId ? 'ticket' : (invoiceId ? 'invoice' : ''));
    const related_id = bookingId || ticketId || invoiceId || '';
    setEditor({
      ...emptyLetter,
      recipient,
      related_type,
      related_id,
    });
    // URL bereinigen (verhindert erneutes Öffnen bei Re-Render)
    const sp = new URLSearchParams(searchParams);
    ['new', 'customer_id', 'supplier_id', 'booking_id', 'ticket_id', 'invoice_id'].forEach((k) => sp.delete(k));
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line
  }, [loading, customers, suppliers]);

  const grouped = useMemo(() => {
    const g = { draft: [], sent: [], archived: [] };
    for (const l of letters) g[l.status]?.push(l);
    return g;
  }, [letters]);

  const openNew = () => setEditor({ ...emptyLetter });

  const openExisting = (l) => {
    setEditor({
      id: l.id,
      subject: l.subject || '',
      body: l.body || '',
      recipient: { ...emptyRecipient, ...(l.recipient || {}) },
      sender_name: l.sender_name || '',
      related_type: l.related_type || '',
      related_id: l.related_id || '',
      template_id: l.template_id || '',
      notes_admin: l.notes_admin || '',
      _status: l.status,
      _letter_number: l.letter_number,
      _auto_generated_from: l.auto_generated_from || null,
    });
  };

  const applyTemplate = (tpl) => {
    setEditor((e) => ({
      ...e,
      subject: tpl.subject || e.subject,
      body: tpl.body || e.body,
      template_id: tpl.id,
    }));
    setShowTemplates(false);
    toast.success(`Vorlage "${tpl.name}" geladen`);
  };

  const saveEditor = async ({ closeAfter = false } = {}) => {
    if (!editor) return null;
    if (!editor.subject.trim()) { toast.error('Betreff fehlt'); return null; }
    if (!editor.body.trim()) { toast.error('Brieftext fehlt'); return null; }
    if (editor.recipient.type !== 'freeform' && !editor.recipient.id) {
      toast.error('Bitte Empfänger auswählen');
      return null;
    }
    if (editor.recipient.type === 'freeform' && !editor.recipient.name) {
      toast.error('Bitte Empfänger-Name eingeben');
      return null;
    }
    setEditorSaving(true);
    try {
      const payload = {
        subject: editor.subject,
        body: editor.body,
        recipient: editor.recipient,
        sender_name: editor.sender_name || null,
        related_type: editor.related_type || null,
        related_id: editor.related_id || null,
        template_id: editor.template_id || null,
        notes_admin: editor.notes_admin || null,
      };
      let saved;
      if (editor.id) {
        const r = await letterApi.update(editor.id, payload);
        saved = r.data;
      } else {
        const r = await letterApi.create(payload);
        saved = r.data;
        setEditor({ ...editor, id: saved.id, _letter_number: saved.letter_number });
      }
      toast.success(`Brief ${saved.letter_number} gespeichert`);
      await loadAll();
      if (closeAfter) setEditor(null);
      return saved;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally {
      setEditorSaving(false);
    }
  };

  const downloadPdf = async (letter) => {
    try {
      const res = await letterApi.pdf(letter.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brief_${letter.letter_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPreview = (letterId) => {
    window.open(letterApi.pdfUrl(letterId), '_blank');
  };

  const sendEmail = async (letter) => {
    if (!letter.recipient?.email) {
      toast.error('Empfänger hat keine E-Mail-Adresse');
      return;
    }
    const isAuto = !!letter.auto_generated_from || !!letter._auto_generated_from;
    const prefix = isAuto ? '⚠️ Dies ist ein AUTO-GENERIERTER Brief. Bitte Inhalt sorgfältig prüfen!\n\n' : '';
    if (!window.confirm(`${prefix}Brief ${letter.letter_number} per E-Mail an ${letter.recipient.email} senden?`)) return;
    try {
      const r = await letterApi.sendEmail(letter.id);
      toast.success(r.data.message || 'E-Mail versendet');
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const archiveLetter = async (letter) => {
    try {
      await letterApi.archive(letter.id);
      toast.success('Brief archiviert');
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteLetter = async (letter) => {
    if (!window.confirm(`Brief ${letter.letter_number} wirklich löschen?`)) return;
    try {
      await letterApi.remove(letter.id);
      toast.success('Brief gelöscht');
      if (editor?.id === letter.id) setEditor(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runAI = async () => {
    if (!aiForm.intent.trim()) { toast.error('Bitte Anliegen (Intent) angeben'); return; }
    setAiRunning(true);
    try {
      const r = await letterApi.aiDraft({
        intent: aiForm.intent.trim(),
        tone: aiForm.tone,
        keywords: aiForm.keywords.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        recipient_hint: aiForm.recipient_hint.trim() || null,
        context: aiForm.context.trim() || null,
      });
      const data = r.data;
      setEditor((e) => ({
        ...(e || { ...emptyLetter }),
        subject: data.subject || e?.subject || '',
        body: data.body || e?.body || '',
      }));
      setShowAI(false);
      toast.success('KI-Entwurf übernommen — bitte prüfen und anpassen.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiRunning(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!newTplName.trim()) { toast.error('Name fehlt'); return; }
    if (!editor) return;
    try {
      await letterTemplateApi.create({
        name: newTplName.trim(),
        category: 'eigene',
        subject: editor.subject,
        body: editor.body,
      });
      toast.success('Als Vorlage gespeichert');
      setShowSaveAsTpl(false);
      setNewTplName('');
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Vorlage "${tpl.name}" wirklich löschen?`)) return;
    try {
      await letterTemplateApi.remove(tpl.id);
      toast.success('Vorlage gelöscht');
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="letters-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#111418]">Briefe</h1>
          <p className="text-sm text-[#525866] mt-1">
            Anschreiben an Kunden, Lieferanten und Behörden. PDF-Ausgabe, E-Mail-Versand und KI-Assistent inklusive.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowTemplates(true)} data-testid="templates-btn">
            <FileText size={14} weight="bold" className="mr-1.5" />
            Vorlagen ({templates.length})
          </Button>
          <Button className="btn-primary" onClick={openNew} data-testid="new-letter-btn">
            <Plus size={14} weight="bold" className="mr-1.5" /> Neuer Brief
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-xs uppercase text-[#8A94A6]">Status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]" data-testid="letter-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="draft">Entwürfe</SelectItem>
            <SelectItem value="sent">Gesendet</SelectItem>
            <SelectItem value="archived">Archiv</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Liste */}
      <div className="space-y-6">
        {['draft', 'sent', 'archived'].map((st) => {
          const list = statusFilter === 'all' ? grouped[st] : (statusFilter === st ? letters : []);
          if (!list || list.length === 0) return null;
          return (
            <div key={st}>
              <h2 className="text-sm font-semibold text-[#525866] mb-2 flex items-center gap-2">
                {st === 'draft' && <Clock size={14} weight="bold" />}
                {st === 'sent' && <CheckCircle size={14} weight="bold" />}
                {st === 'archived' && <Archive size={14} weight="bold" />}
                {STATUS_LABELS[st]} <span className="text-[#8A94A6]">({list.length})</span>
              </h2>
              <div className="space-y-2">
                {list.map((l) => {
                  const RIcon = RECIPIENT_ICONS[l.recipient?.type] || MapPin;
                  return (
                    <div
                      key={l.id}
                      className="card-swiss p-3 flex flex-wrap items-center gap-3 hover:border-[#E60000] transition-colors cursor-pointer"
                      onClick={() => openExisting(l)}
                      data-testid={`letter-row-${l.id}`}
                    >
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-[#8A94A6]">{l.letter_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm border ${STATUS_STYLES[l.status]}`}>
                            {STATUS_LABELS[l.status]}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-[#F7F8FA] text-[#525866] rounded-sm flex items-center gap-1">
                            <RIcon size={10} weight="bold" /> {RECIPIENT_LABELS[l.recipient?.type] || '—'}
                          </span>
                          {l.auto_generated_from && (
                            <span
                              className="text-xs px-2 py-0.5 bg-[#FFF7E6] text-[#B8860B] rounded-sm border border-[#F4D48A]"
                              title="Automatisch erstellter Entwurf — bitte vor Versand prüfen"
                              data-testid={`letter-auto-badge-${l.id}`}
                            >
                              ✨ Auto
                            </span>
                          )}
                        </div>
                        <p className="font-semibold text-[#111418] mt-1 truncate">{l.subject}</p>
                        <p className="text-xs text-[#525866] mt-0.5">
                          {l.recipient?.name || l.recipient?.company || '—'}
                          {l.recipient?.city && ` · ${l.recipient.city}`}
                          {' · '}
                          {new Date(l.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" title="PDF ansehen" onClick={() => openPreview(l.id)} data-testid={`letter-preview-${l.id}`}>
                          <FilePdf size={16} weight="bold" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Herunterladen" onClick={() => downloadPdf(l)}>
                          <FileText size={16} weight="bold" />
                        </Button>
                        {l.recipient?.email && (
                          <Button size="sm" variant="ghost" title="Per E-Mail senden" onClick={() => sendEmail(l)} data-testid={`letter-email-${l.id}`}>
                            <EnvelopeSimple size={16} weight="bold" />
                          </Button>
                        )}
                        {l.status !== 'archived' && (
                          <Button size="sm" variant="ghost" title="Archivieren" onClick={() => archiveLetter(l)}>
                            <Archive size={16} weight="bold" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title="Löschen" onClick={() => deleteLetter(l)} className="text-[#E60000]" data-testid={`letter-delete-${l.id}`}>
                          <Trash size={16} weight="bold" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {!loading && letters.length === 0 && (
          <div className="card-swiss p-12 text-center">
            <FileText size={40} weight="thin" className="mx-auto text-[#C8CBD0]" />
            <p className="text-[#525866] mt-3">Noch keine Briefe. Klicke &bdquo;Neuer Brief&ldquo; um zu starten.</p>
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="letter-editor-dialog">
          {editor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap">
                  {editor.id ? `Brief ${editor._letter_number || ''}` : 'Neuer Brief'}
                  {editor._status && (
                    <span className={`text-xs px-2 py-0.5 rounded-sm border ${STATUS_STYLES[editor._status]}`}>
                      {STATUS_LABELS[editor._status]}
                    </span>
                  )}
                  {editor.related_type && editor.related_id && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-sm bg-[#F7F8FA] text-[#525866] border border-[#E5E7EB] cursor-pointer hover:border-[#E60000]"
                      title="Zur verknüpften Seite"
                      onClick={() => {
                        const map = { booking: 'bookings', ticket: 'tickets', invoice: 'invoices' };
                        const path = map[editor.related_type];
                        if (path) navigate(`/${path}/${editor.related_id}`);
                      }}
                      data-testid="related-badge"
                    >
                      Verknüpft: {editor.related_type} #{editor.related_id.substring(0, 8)}
                    </span>
                  )}
                  {editor._auto_generated_from && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-sm bg-[#FFF7E6] text-[#B8860B] border border-[#F4D48A]"
                      title="Dieser Brief wurde automatisch als Entwurf erstellt. Bitte prüfen und bei Bedarf anpassen, bevor er versendet wird."
                      data-testid="auto-generated-badge"
                    >
                      ✨ Auto-generiert
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Empfänger */}
                <div className="space-y-3">
                  <div>
                    <Label className="overline">Empfänger-Typ</Label>
                    <Select
                      value={editor.recipient.type}
                      onValueChange={(v) => setEditor({ ...editor, recipient: { ...emptyRecipient, type: v, country: 'Deutschland' } })}
                    >
                      <SelectTrigger data-testid="recipient-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Kunde</SelectItem>
                        <SelectItem value="supplier">Lieferant</SelectItem>
                        <SelectItem value="freeform">Freie Adresse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {editor.recipient.type === 'customer' && (
                    <div>
                      <Label className="overline">Kunde wählen *</Label>
                      <Select
                        value={editor.recipient.id || ''}
                        onValueChange={(v) => {
                          const c = customers.find((x) => x.id === v);
                          setEditor({
                            ...editor,
                            recipient: {
                              ...emptyRecipient,
                              type: 'customer',
                              id: v,
                              name: c ? `${c.first_name} ${c.last_name}` : '',
                              address: c?.address || '',
                              postal_code: c?.postal_code || '',
                              city: c?.city || '',
                              country: c?.country || 'Deutschland',
                              email: c?.email || '',
                            },
                          });
                        }}
                      >
                        <SelectTrigger data-testid="recipient-customer-select"><SelectValue placeholder="— wählen —" /></SelectTrigger>
                        <SelectContent className="max-h-80">
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.first_name} {c.last_name} {c.city && ` · ${c.city}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {editor.recipient.type === 'supplier' && (
                    <div>
                      <Label className="overline">Lieferant wählen *</Label>
                      <Select
                        value={editor.recipient.id || ''}
                        onValueChange={(v) => {
                          const s = suppliers.find((x) => x.id === v);
                          setEditor({
                            ...editor,
                            recipient: {
                              ...emptyRecipient,
                              type: 'supplier',
                              id: v,
                              company: s?.name || s?.company || '',
                              name: s?.contact_name || s?.name || '',
                              address: s?.address || '',
                              postal_code: s?.postal_code || '',
                              city: s?.city || '',
                              country: s?.country || 'Deutschland',
                              email: s?.email || '',
                            },
                          });
                        }}
                      >
                        <SelectTrigger data-testid="recipient-supplier-select"><SelectValue placeholder="— wählen —" /></SelectTrigger>
                        <SelectContent className="max-h-80">
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name || s.company}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Editable Adresse */}
                  <div>
                    <Label className="overline">Firma / Institution</Label>
                    <Input value={editor.recipient.company || ''} onChange={(e) => setEditor({ ...editor, recipient: { ...editor.recipient, company: e.target.value } })} data-testid="recipient-company-input" />
                  </div>
                  <div>
                    <Label className="overline">Name *</Label>
                    <Input value={editor.recipient.name || ''} onChange={(e) => setEditor({ ...editor, recipient: { ...editor.recipient, name: e.target.value } })} data-testid="recipient-name-input" />
                  </div>
                  <div>
                    <Label className="overline">Straße + Nr.</Label>
                    <Input value={editor.recipient.address || ''} onChange={(e) => setEditor({ ...editor, recipient: { ...editor.recipient, address: e.target.value } })} data-testid="recipient-address-input" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="overline">PLZ</Label>
                      <Input value={editor.recipient.postal_code || ''} onChange={(e) => setEditor({ ...editor, recipient: { ...editor.recipient, postal_code: e.target.value } })} data-testid="recipient-plz-input" />
                    </div>
                    <div className="col-span-2">
                      <Label className="overline">Ort</Label>
                      <Input value={editor.recipient.city || ''} onChange={(e) => setEditor({ ...editor, recipient: { ...editor.recipient, city: e.target.value } })} data-testid="recipient-city-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="overline">E-Mail (für Versand)</Label>
                    <Input type="email" value={editor.recipient.email || ''} onChange={(e) => setEditor({ ...editor, recipient: { ...editor.recipient, email: e.target.value } })} data-testid="recipient-email-input" />
                  </div>
                </div>

                {/* Betreff + Body */}
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setShowTemplates(true)} data-testid="load-template-btn">
                      <FileText size={14} weight="bold" className="mr-1.5" /> Vorlage laden
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAI(true)} className="border-[#E60000] text-[#E60000] hover:bg-[#E60000]/10" data-testid="ai-draft-btn">
                      <Sparkle size={14} weight="bold" className="mr-1.5" /> KI-Assistent
                    </Button>
                  </div>
                  <div>
                    <Label className="overline">Betreff *</Label>
                    <Input value={editor.subject} onChange={(e) => setEditor({ ...editor, subject: e.target.value })} data-testid="letter-subject-input" />
                  </div>
                  <div>
                    <Label className="overline">Brieftext *</Label>
                    <textarea
                      className="w-full min-h-[350px] border border-[#E5E7EB] rounded-sm px-3 py-2 text-sm font-serif leading-relaxed"
                      value={editor.body}
                      onChange={(e) => setEditor({ ...editor, body: e.target.value })}
                      placeholder="Sehr geehrte/r ..."
                      data-testid="letter-body-input"
                    />
                    <p className="text-xs text-[#8A94A6] mt-1">Absätze mit Leerzeile trennen. Wird formatiert im PDF gerendert.</p>
                  </div>
                  <div>
                    <Label className="overline">Interne Notiz (nicht auf PDF)</Label>
                    <Input value={editor.notes_admin} onChange={(e) => setEditor({ ...editor, notes_admin: e.target.value })} data-testid="letter-notes-input" />
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 flex-wrap justify-between">
                <div className="flex gap-2 flex-wrap">
                  {editor.id && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openPreview(editor.id)} data-testid="editor-preview-btn">
                        <FilePdf size={14} weight="bold" className="mr-1.5" /> PDF-Vorschau
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf({ id: editor.id, letter_number: editor._letter_number })}>
                        <FileText size={14} weight="bold" className="mr-1.5" /> Herunterladen
                      </Button>
                      {editor.recipient?.email && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const saved = await saveEditor({});
                            if (saved) sendEmail(saved);
                          }}
                          data-testid="editor-email-btn"
                        >
                          <EnvelopeSimple size={14} weight="bold" className="mr-1.5" /> Per E-Mail
                        </Button>
                      )}
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setShowSaveAsTpl(true)} data-testid="save-as-template-btn">
                    <Copy size={14} weight="bold" className="mr-1.5" /> Als Vorlage speichern
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditor(null)} disabled={editorSaving}>Abbrechen</Button>
                  <Button className="btn-primary" onClick={() => saveEditor({ closeAfter: false })} disabled={editorSaving} data-testid="save-letter-btn">
                    {editorSaving ? 'Wird gespeichert…' : (editor.id ? 'Speichern' : 'Brief anlegen')}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle>Brief-Vorlagen</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {templates.map((t) => (
              <div
                key={t.id}
                className="card-swiss p-3 flex items-center justify-between gap-2 hover:border-[#E60000] transition-colors"
                data-testid={`template-row-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#111418]">
                    {t.name}
                    {t.is_system && <span className="ml-2 text-xs px-1.5 py-0.5 bg-[#F7F8FA] text-[#525866] rounded-sm">System</span>}
                  </p>
                  <p className="text-xs text-[#8A94A6]">{t.category} · {t.subject || '(kein Standard-Betreff)'}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditor((prev) => ({
                        ...(prev || { ...emptyLetter }),
                        subject: t.subject || prev?.subject || '',
                        body: t.body || prev?.body || '',
                        template_id: t.id,
                      }));
                      setShowTemplates(false);
                      toast.success(`Vorlage "${t.name}" geladen`);
                    }}
                    data-testid={`use-template-${t.id}`}
                  >
                    Übernehmen
                  </Button>
                  {!t.is_system && (
                    <Button size="sm" variant="ghost" className="text-[#E60000]" onClick={() => deleteTemplate(t)}>
                      <Trash size={14} weight="bold" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Assistant Dialog */}
      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="max-w-lg" data-testid="ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} weight="bold" className="text-[#E60000]" />
              KI-Textentwurf
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Anliegen (Intent) *</Label>
              <Input
                value={aiForm.intent}
                onChange={(e) => setAiForm({ ...aiForm, intent: e.target.value })}
                placeholder="z.B. Zahlungsaufforderung, Terminbestätigung, Beschwerde-Antwort"
                data-testid="ai-intent-input"
              />
            </div>
            <div>
              <Label className="overline">Ton</Label>
              <Select value={aiForm.tone} onValueChange={(v) => setAiForm({ ...aiForm, tone: v })}>
                <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="formell">Formell</SelectItem>
                  <SelectItem value="freundlich">Freundlich</SelectItem>
                  <SelectItem value="dringend">Dringend</SelectItem>
                  <SelectItem value="sachlich">Sachlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Stichworte (kommagetrennt)</Label>
              <Input
                value={aiForm.keywords}
                onChange={(e) => setAiForm({ ...aiForm, keywords: e.target.value })}
                placeholder="Rechnung 2026-042, 542,80 EUR, Frist 14 Tage"
                data-testid="ai-keywords-input"
              />
            </div>
            <div>
              <Label className="overline">Empfänger-Hinweis</Label>
              <Input
                value={aiForm.recipient_hint}
                onChange={(e) => setAiForm({ ...aiForm, recipient_hint: e.target.value })}
                placeholder="z.B. Privatkunde, Firmenkunde, Werkstatt-Lieferant, Bußgeldstelle"
                data-testid="ai-recipient-input"
              />
            </div>
            <div>
              <Label className="overline">Zusatzkontext (optional)</Label>
              <textarea
                className="w-full min-h-[70px] border border-[#E5E7EB] rounded-sm px-3 py-2 text-sm"
                value={aiForm.context}
                onChange={(e) => setAiForm({ ...aiForm, context: e.target.value })}
                placeholder="Weitere Details, die im Brief erwähnt werden sollen"
                data-testid="ai-context-input"
              />
            </div>
            <div className="p-2 bg-[#FFF7E6] border border-[#F4D48A] rounded-sm text-xs text-[#B8860B]">
              <Warning size={12} weight="bold" className="inline mr-1" />
              Der Entwurf ersetzt Betreff und Brieftext. Bitte immer nachprüfen und ggf. anpassen.
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setShowAI(false)} disabled={aiRunning}>Abbrechen</Button>
              <Button className="btn-primary" onClick={runAI} disabled={aiRunning} data-testid="ai-generate-btn">
                {aiRunning ? 'KI arbeitet…' : 'Entwurf erstellen'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveAsTpl} onOpenChange={setShowSaveAsTpl}>
        <DialogContent className="max-w-sm" data-testid="save-tpl-dialog">
          <DialogHeader>
            <DialogTitle>Als Vorlage speichern</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Name *</Label>
              <Input
                value={newTplName}
                onChange={(e) => setNewTplName(e.target.value)}
                placeholder="z.B. Mahnung 2. Stufe"
                data-testid="new-tpl-name-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setShowSaveAsTpl(false)}>Abbrechen</Button>
              <Button className="btn-primary" onClick={saveAsTemplate} data-testid="save-tpl-btn">Speichern</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Letters;
